// src/components/Dashboard/Dashboard.js - Updated with Manufacturing Button
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import WarehouseDetailModal from '../Warehouses/WarehouseDetailModal';
import {
  Package,
  Warehouse,
  AlertTriangle,
  Activity,
  RefreshCw,
  LogOut,
  Users,
  BarChart3,
  TrendingUp,
  Globe,
  Cpu  // Icon for Manufacturing
} from 'lucide-react';

const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // State
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const [salesData, setSalesData] = useState({
    total_orders: 0,
    total_units: 0,
    total_revenue: 0
  });

  // Modal states
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard/real-time-inventory`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
      setLastUpdated(new Date());
      console.log('✅ Dashboard data loaded');

    } catch (error) {
      console.error('❌ Dashboard fetch error:', error);
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Amazon sales
  const fetchAmazonSales = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard/amazon-sales`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.warn('Could not fetch Amazon sales');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSalesData(data.summary);
        console.log('✅ Amazon sales loaded');
      }

    } catch (error) {
      console.error('⚠️ Amazon sales fetch error:', error);
    }
  };

  // Handle warehouse click
  const handleWarehouseClick = (warehouse) => {
    setSelectedWarehouse({
      id: warehouse.warehouse?.id,
      name: warehouse.warehouse?.name || `Warehouse ${warehouse.warehouse?.id}`,
      location: warehouse.warehouse?.location || 'Location not specified'
    });
    setIsWarehouseModalOpen(true);
  };

  // Handle Amazon click
  const handleAmazonClick = () => {
    navigate('/amazon');
  };

  // Load data on mount
  useEffect(() => {
    fetchDashboardData();
    fetchAmazonSales();
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div>
            <h1 style={{
              fontSize: '1.875rem',
              fontWeight: 'bold',
              color: '#1f2937',
              margin: 0
            }}>
              📦 Inventory Dashboard
            </h1>
            <p style={{
              color: '#6b7280',
              margin: '0.25rem 0 0 0'
            }}>
              Welcome back, {user?.full_name}
            </p>
            {lastUpdated && (
              <p style={{
                fontSize: '0.875rem',
                color: '#9ca3af',
                margin: '0.25rem 0 0 0'
              }}>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>

              {/* Shipping Link */}
              <Link
              to="/shipping"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#0284c7',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <Package size={16} />
              Shipping
            </Link>

            {/* Planning Link */}
            <Link
              to="/planning"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#2563eb',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <BarChart3 size={16} />
              Planning
            </Link>

            {/* Sales Link */}
            <Link
              to="/sales"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#059669',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <TrendingUp size={16} />
              Sales
            </Link>

            {/* Manufacturing Link - NEW */}
            <Link
              to="/manufacturing"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#7c3aed',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <Cpu size={16} />
              Manufacturing
            </Link>

            {/* Users Link (Admin only) */}
            {isAdmin() && (
              <Link
                to="/users"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  textDecoration: 'none',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <Users size={16} />
                Users
              </Link>
            )}

            {/* Refresh Button */}
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>

            {/* Logout Button */}
            <button
              onClick={logout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'white',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {/* Error State */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <AlertTriangle size={20} style={{ color: '#dc2626' }} />
            <div>
              <h3 style={{ margin: 0, color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>
                Error Loading Dashboard
              </h3>
              <p style={{ margin: '0.25rem 0 0 0', color: '#7f1d1d', fontSize: '0.875rem' }}>
                {error}
              </p>
              <button
                onClick={fetchDashboardData}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p style={{ color: '#6b7280' }}>Loading dashboard...</p>
            </div>
          </div>
        )}

        {/* Dashboard Content */}
        {dashboardData && !loading && (
          <>
            {/* Summary Cards - Updated to include Manufacturing */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {/* Total Items */}
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <Package size={24} style={{ color: '#2563eb' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Total Inventory
                  </h3>
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '0.25rem'
                }}>
                  {dashboardData.summary?.total_in_warehouses?.toLocaleString() || 0}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  {dashboardData.summary?.unique_skus || 0} unique SKUs
                </div>
              </div>

              {/* Warehouses */}
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <Warehouse size={24} style={{ color: '#059669' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Warehouses
                  </h3>
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '0.25rem'
                }}>
                  {dashboardData.summary?.successful_warehouses || 0}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  Active warehouses
                </div>
              </div>

              {/* Manufacturing - NEW Clickable Card */}
              <Link
                to="/manufacturing"
                style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  textDecoration: 'none',
                  display: 'block',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <Cpu size={24} style={{ color: '#7c3aed' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Manufacturing
                  </h3>
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#7c3aed',
                  marginBottom: '0.25rem'
                }}>
                  Kits
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  Manage devices →
                </div>
              </Link>

              {/* Low Stock */}
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <AlertTriangle size={24} style={{ color: '#d97706' }} />
                  <h3 style={{
                    margin: 0,
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Low Stock Items
                  </h3>
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#d97706',
                  marginBottom: '0.25rem'
                }}>
                  {dashboardData.summary?.total_low_stock_items || 0}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  Need attention
                </div>
              </div>
            </div>

            {/* Amazon Sales (30 Days) - Clickable */}
            <Link
              to="/sales"
              style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                textDecoration: 'none',
                display: 'block',
                transition: 'all 0.2s',
                cursor: 'pointer',
                marginBottom: '2rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <TrendingUp size={24} style={{ color: '#059669' }} />
                <h3 style={{
                  margin: 0,
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Amazon Sales
                </h3>
              </div>
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '0.25rem'
              }}>
                {salesData.total_orders.toLocaleString()}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#6b7280'
              }}>
                {salesData.total_units.toLocaleString()} units • ${salesData.total_revenue.toLocaleString()} (30d) →
              </div>
            </Link>

            {/* Warehouses List */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Warehouse size={20} />
                  Warehouse Status
                </h2>
              </div>

              <div style={{ padding: '1.5rem' }}>
                <div style={{
                  display: 'grid',
                  gap: '1rem'
                }}>
                  {/* Existing Warehouses */}
                  {dashboardData.warehouses && dashboardData.warehouses.length > 0 && (
                    dashboardData.warehouses.map((warehouse, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => handleWarehouseClick(warehouse)}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div>
                          <h3 style={{
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: '500',
                            color: '#1f2937'
                          }}>
                            {warehouse.warehouse?.name || `Warehouse ${warehouse.warehouse?.id}`}
                          </h3>
                          <p style={{
                            margin: '0.25rem 0 0 0',
                            fontSize: '0.875rem',
                            color: '#6b7280'
                          }}>
                            {warehouse.warehouse?.location || 'Location not specified'}
                          </p>
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem'
                        }}>
                          {warehouse.success ? (
                            <>
                              <div style={{
                                textAlign: 'right'
                              }}>
                                <div style={{
                                  fontSize: '1.125rem',
                                  fontWeight: '600',
                                  color: '#1f2937'
                                }}>
                                  {warehouse.stats?.total_items?.toLocaleString() || 0}
                                </div>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280'
                                }}>
                                  items
                                </div>
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}>
                                <Activity size={16} style={{ color: '#16a34a' }} />
                                <span style={{
                                  fontSize: '0.875rem',
                                  color: '#16a34a',
                                  fontWeight: '500'
                                }}>
                                  Online
                                </span>
                              </div>
                            </>
                          ) : (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <Activity size={16} style={{ color: '#dc2626' }} />
                              <span style={{
                                fontSize: '0.875rem',
                                color: '#dc2626',
                                fontWeight: '500'
                              }}>
                                Offline
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Amazon FBA Warehouse */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      border: '2px solid #ff9900',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor: '#fff8f0'
                    }}
                    onClick={handleAmazonClick}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff3e0';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff8f0';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Globe size={20} style={{ color: '#ff9900' }} />
                      <div>
                        <h3 style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: '500',
                          color: '#1f2937'
                        }}>
                          Amazon FBA
                        </h3>
                        <p style={{
                          margin: '0.25rem 0 0 0',
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>
                          Fulfillment by Amazon • EU & NA Regions
                        </p>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <div style={{
                        textAlign: 'right'
                      }}>
                        <div style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#ff9900'
                        }}>
                          View Inventory
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6b7280'
                        }}>
                          Click to view
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        <Activity size={16} style={{ color: '#16a34a' }} />
                        <span style={{
                          fontSize: '0.875rem',
                          color: '#16a34a',
                          fontWeight: '500'
                        }}>
                          Online
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Empty state if no warehouses */}
                  {(!dashboardData.warehouses || dashboardData.warehouses.length === 0) && (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: '#6b7280'
                    }}>
                      <Warehouse size={48} style={{ opacity: 0.5, margin: '0 auto 0.75rem' }} />
                      <p>No warehouses configured</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Warehouse Detail Modal */}
      <WarehouseDetailModal
        isOpen={isWarehouseModalOpen}
        onClose={() => setIsWarehouseModalOpen(false)}
        warehouseId={selectedWarehouse?.id}
        warehouseName={selectedWarehouse?.name}
      />
    </div>
  );
};

export default Dashboard;